import { useCallback, useEffect, useState, type MouseEvent } from 'react';
import { useNavigate, generatePath } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useSWR, { useSWRConfig } from 'swr';
import { PencilIcon, TrashIcon, ListBulletsIcon } from '@phosphor-icons/react';
import { AutoHideOnScroll, Table, toast, SkeletonType } from '@/components';
import { EngineService } from '@/services/engine';
import { alertKey, useAlertsStore } from '@/stores';
import { useClampedPage, useTableState, useOpenInNewWindow } from '@/hooks';
import { AlertRulesHeader } from './components/AlertRulesHeader';
import { AlertRulesActionBar } from './components/AlertRulesActionBar';
import { useAlertRulesColumns } from './hooks/useAlertRulesColumns';
import { useAlertRulesSelection } from './hooks/useAlertRulesSelection';
import { useAlertRulesFilters } from './hooks/useAlertRulesFilters';
import { AppRoutes } from '@/constants/routes';
import { FullAlertRule } from '@/types/alert';
import { TableAction, TableEmptyStateConfig } from '@/types/table';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { PAGE_SIZE, CACHE_KEY } from './constants';
import { revalidateAlertUnitCapacity } from '@/features/SettingsView/hooks/useConfiguration';

const SKELETON_TYPE_MAP = {
    checkbox: SkeletonType.CHECKBOX,
    status: SkeletonType.BADGE,
    lastTriggered: SkeletonType.ICON_TEXT,
    createdBy: SkeletonType.AVATAR,
} as const;

export function AlertRulesList() {
    const navigate = useNavigate();
    const { openUrl } = useOpenInNewWindow();
    const { t } = useTranslation(I18nNamespace.ALERT_RULES);
    const { mutate } = useSWRConfig();
    const [scrollContainer, setScrollContainer] =
        useState<HTMLDivElement | null>(null);

    const tableState = useTableState({
        initialPageSize: PAGE_SIZE,
    });

    const {
        dateRange,
        statusFilter,
        sortOption,
        setDateRange,
        setStatusFilter,
        setSortOption,
        getQueryParams: getFilterParams,
    } = useAlertRulesFilters();

    const { data, isLoading } = useSWR(
        [
            CACHE_KEY,
            tableState.page,
            tableState.pageSize,
            tableState.debouncedSearch,
            dateRange,
            statusFilter,
            sortOption,
        ],
        ([_, page, size, search]) =>
            EngineService.getAlertRules({
                page,
                size,
                search: search || undefined,
                ...getFilterParams(),
            }),
        {
            revalidateOnFocus: false,
        },
    );

    const {
        selectedIds,
        isMultiSelectMode,
        selectedCount,
        toggleItem,
        selectAll,
        clearSelection,
        enterMultiSelectMode,
        exitMultiSelectMode,
    } = useAlertRulesSelection();

    const revalidateData = useCallback(() => {
        mutate((key) => Array.isArray(key) && key[0] === CACHE_KEY);
    }, [mutate]);

    const latestAlert = useAlertsStore((state) => state.recentAlerts[0]);
    const latestAlertKey = latestAlert ? alertKey(latestAlert) : undefined;

    useEffect(() => {
        if (!latestAlertKey) return;
        revalidateData();
    }, [latestAlertKey, revalidateData]);

    const handleRowClick = useCallback(
        (row: FullAlertRule, event?: MouseEvent<HTMLTableRowElement>) => {
            if (isMultiSelectMode) {
                toggleItem(row.id);
                return;
            }
            openUrl(generatePath(AppRoutes.ALERT_RULE, { id: row.id }), event, {
                preserveSearchParams: false,
            });
        },
        [isMultiSelectMode, openUrl, toggleItem],
    );

    const handleEditAlertRule = (row: FullAlertRule) => {
        navigate(AppRoutes.ALERT_RULES_EDIT.replace(':id', row.id));
    };

    const handleDeleteAlertRule = async (row: FullAlertRule) => {
        try {
            await EngineService.deleteAlertRule(row.id);
            toast.success(t('toast.deleteSuccess'));
            await revalidateAlertUnitCapacity();
            revalidateData();
        } catch {
            toast.error(t('toast.deleteError'));
        }
    };

    const getRowActions = useCallback(
        (row: FullAlertRule): TableAction<FullAlertRule>[] => [
            {
                label: t('actions.edit'),
                icon: <PencilIcon className="size-4" weight="regular" />,
                onClick: handleEditAlertRule,
            },
            {
                label: t('actions.delete'),
                icon: <TrashIcon className="size-4" weight="regular" />,
                onClick: handleDeleteAlertRule,
                variant: 'danger',
            },
        ],
        [t, handleEditAlertRule, handleDeleteAlertRule],
    );

    const columns = useAlertRulesColumns({
        isMultiSelectMode,
        selectedIds,
    });

    const totalCount = data?.page.totalItems || 0;

    useClampedPage({
        page: tableState.page,
        pageSize: tableState.pageSize,
        totalCount: data?.page.totalItems,
        onPageChange: tableState.onPageChange,
    });

    const emptyStateConfig: TableEmptyStateConfig = {
        icon: ListBulletsIcon,
        title: t('empty.title'),
        description: t('empty.description'),
        buttonText: t('empty.button'),
        onButtonClick: () => navigate(AppRoutes.ALERT_RULES_CREATE),
    };

    return (
        <div className="h-full flex flex-col relative">
            <AutoHideOnScroll container={scrollContainer}>
                <AlertRulesActionBar
                    items={data?.items ?? []}
                    totalCount={totalCount}
                    selectedIds={selectedIds}
                    selectedCount={selectedCount}
                    isMultiSelectMode={isMultiSelectMode}
                    enterMultiSelectMode={enterMultiSelectMode}
                    exitMultiSelectMode={exitMultiSelectMode}
                    selectAll={selectAll}
                    clearSelection={clearSelection}
                    dateRange={dateRange}
                    onDateRangeChange={setDateRange}
                    statusFilter={statusFilter}
                    onStatusFilterChange={setStatusFilter}
                    sortOption={sortOption}
                    onSortOptionChange={setSortOption}
                    onSearchSubmit={tableState.onSearchChange}
                    onRevalidate={revalidateData}
                />
            </AutoHideOnScroll>

            <div
                ref={setScrollContainer}
                className="flex-1 min-h-0 overflow-y-auto px-4 panels:px-8 pb-32"
            >
                <div className="pt-20" />
                <div className="mx-auto w-full max-w-content rounded-lg bg-grey-700 overflow-hidden flex flex-col">
                    <AlertRulesHeader />

                    <div className="overflow-auto pb-8">
                        <Table
                            data={data?.items || []}
                            columns={columns}
                            actions={
                                isMultiSelectMode ? undefined : getRowActions
                            }
                            isLoading={isLoading}
                            page={tableState.page}
                            pageSize={tableState.pageSize}
                            totalCount={totalCount}
                            skeletonTypeMap={SKELETON_TYPE_MAP}
                            emptyState={
                                tableState.debouncedSearch
                                    ? undefined
                                    : emptyStateConfig
                            }
                            onPageChange={tableState.onPageChange}
                            onRowClick={handleRowClick}
                        />
                    </div>
                </div>
            </div>

            <div className="absolute bottom-0 left-0 right-0 h-64 pointer-events-none bg-gradient-to-t from-grey-700 to-transparent" />
        </div>
    );
}
